import React, { useEffect, useState } from 'react'
import { Table, Button, Space, Input, Modal, Form, Select, InputNumber, message, Popconfirm, Tag, Divider, Card, Row, Col, Badge } from 'antd'
import { PlusOutlined, ArrowUpOutlined, ArrowDownOutlined, InboxOutlined, ExclamationCircleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'

const { Search } = Input
const { confirm } = Modal

interface Warehouse { id: number; name: string }
interface Product { id: number; name: string; unit: string }
interface PendingReceipt { id: number; order_no: string; warehouse_id: number; supplier_id: number; expected_date: string; items: Array<{ id: number; product_id: number; product_name?: string; product_image?: string; specs?: string; quantity: number; received_qty: number; pending_qty: number; unit_price: number; total_price: number }>; status: number; created_at: string }
interface InventoryItem { id: number; warehouse_id: number; warehouse?: { id: number; name: string; type: number }; product_id: number; product?: { id: number; name: string; code: string; main_image: string }; batch_no: string; quantity: string; cost_price: string; expire_date: string | null; warning_qty: string; created_at: string; updated_at: string }
interface StockLog { id: number; warehouse_id: number; warehouse_name: string; product_id: number; product_name: string; batch_no: string; log_type: number; quantity: number; unit_price: number; remark: string; status: number; created_at: string; auditor_name?: string }
interface LossItem { id: number; warehouse_id: number; warehouse_name: string; product_id: number; product_name: string; batch_no: string; quantity: number; unit_price: number; total_amount: number; remark: string; status: number; created_at: string; auditor_id?: number }

interface InventoryApi {
  getWarehouses: () => Promise<any>
  getPending: () => Promise<any>
  stockIn: (data: any) => Promise<any>
  getList: (params: any) => Promise<any>
  getSummary: () => Promise<any>
  adjust: (data: any) => Promise<any>
  out: (data: any) => Promise<any>
  reportLoss: (data: any) => Promise<any>
  getLossPending: () => Promise<any>
  auditLoss: (data: any) => Promise<any>
  getLogs: (params: any) => Promise<any>
}

const inventoryApi: InventoryApi = {
  getWarehouses: () => fetch('/api/inventory/warehouses').then(r => r.json()),
  getPending: () => fetch('/api/inventory/pending').then(r => r.json()),
  stockIn: (data) => fetch('/api/inventory/stock-in', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(r => r.json()),
  getList: (params) => fetch('/api/inventory/list?' + new URLSearchParams(params as any)).then(r => r.json()),
  getSummary: () => fetch('/api/inventory/summary').then(r => r.json()),
  adjust: (data) => fetch('/api/inventory/adjust', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(r => r.json()),
  out: (data) => fetch('/api/inventory/out', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(r => r.json()),
  reportLoss: (data) => fetch('/api/inventory/report-loss', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(r => r.json()),
  getLossPending: () => fetch('/api/inventory/report-loss/pending').then(r => r.json()),
  auditLoss: (data) => fetch('/api/inventory/report-loss/audit', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(r => r.json()),
  getLogs: (params) => fetch('/api/inventory/logs?' + new URLSearchParams(params as any)).then(r => r.json()),
}

const Inventory: React.FC = () => {
  const [activeTab, setActiveTab] = useState('list')
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [loading, setLoading] = useState(false)
  const [pendingLoading, setPendingLoading] = useState(false)
  const [lossLoading, setLossLoading] = useState(false)
  const [inModalOpen, setInModalOpen] = useState(false)
  const [outModalOpen, setOutModalOpen] = useState(false)
  const [adjustModalOpen, setAdjustModalOpen] = useState(false)
  const [batchModalOpen, setBatchModalOpen] = useState(false)
  const [selectedWarehouse, setSelectedWarehouse] = useState<number | undefined>()
  const [pendingReceipts, setPendingReceipts] = useState<PendingReceipt[]>([])
  const [selectedReceipt, setSelectedReceipt] = useState<PendingReceipt | null>(null)
  const [inForm] = Form.useForm()
  const [outForm] = Form.useForm()
  const [adjustForm] = Form.useForm()
  const [inventoryData, setInventoryData] = useState<InventoryItem[]>([])
  const [summaryData, setSummaryData] = useState<any[]>([])
  const [logData, setLogData] = useState<StockLog[]>([])
  const [lossData, setLossData] = useState<LossItem[]>([])
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 })
  const [logPagination, setLogPagination] = useState({ current: 1, pageSize: 20, total: 0 })
  const [keyword, setKeyword] = useState('')
  const [batchDetail, setBatchDetail] = useState<{ product_name: string; batches: any[] } | null>(null)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)

  useEffect(() => {
    const role = localStorage.getItem('role')
    setIsSuperAdmin(role === 'admin' || role === 'super_admin')
    fetchWarehouses()
    fetchInventory(pagination.current, pagination.pageSize)
    fetchSummary()
    fetchPending()
  }, [])

  const fetchWarehouses = async () => {
    try {
      const res = await inventoryApi.getWarehouses()
      if (res.code === 200) setWarehouses(res.data || [])
    } catch { message.error('获取仓库列表失败') }
  }

  const fetchPending = async () => {
    setPendingLoading(true)
    try {
      const res = await inventoryApi.getPending()
      if (res.code === 200) setPendingReceipts(res.data || [])
    } catch { message.error('获取待入库清单失败') }
    finally { setPendingLoading(false) }
  }

  const fetchInventory = async (page = 1, pageSize = 20) => {
    setLoading(true)
    try {
      const params: any = { page, page_size: pageSize }
      if (selectedWarehouse) params.warehouse_id = selectedWarehouse
      if (keyword) params.keyword = keyword
      const res = await inventoryApi.getList(params)
      if (res.code === 200) {
        setInventoryData(res.data?.list || [])
        setPagination({ ...pagination, current: page, pageSize, total: res.data?.total || 0 })
      } else { message.error(res.message || '获取库存列表失败') }
    } catch { message.error('获取库存列表失败') }
    finally { setLoading(false) }
  }

  const fetchSummary = async () => {
    try {
      const res = await inventoryApi.getSummary()
      if (res.code === 200) setSummaryData(res.data || [])
    } catch {}
  }

  const fetchLogs = async (page = 1, pageSize = 20) => {
    setLoading(true)
    try {
      const params: any = { page, page_size: pageSize }
      if (selectedWarehouse) params.warehouse_id = selectedWarehouse
      const res = await inventoryApi.getLogs(params)
      if (res.code === 200) {
        setLogData(res.data?.list || [])
        setLogPagination({ ...logPagination, current: page, pageSize, total: res.data?.total || 0 })
      }
    } catch { message.error('获取日志失败') }
    finally { setLoading(false) }
  }

  const fetchLossPending = async () => {
    setLossLoading(true)
    try {
      const res = await inventoryApi.getLossPending()
      if (res.code === 200) setLossData(res.data || [])
    } catch { message.error('获取待审核报损列表失败') }
    finally { setLossLoading(false) }
  }

  // Tab switching
  useEffect(() => {
    if (activeTab === 'pending') fetchPending()
    else if (activeTab === 'loss') fetchLossPending()
    else if (activeTab === 'logs') fetchLogs(logPagination.current, logPagination.pageSize)
    else fetchInventory(pagination.current, pagination.pageSize)
  }, [activeTab])

  const [pendingConfirming, setPendingConfirming] = useState<number | null>(null)
  const [stockInConfirmOpen, setStockInConfirmOpen] = useState(false)
  const [stockInReceipt, setStockInReceipt] = useState<PendingReceipt | null>(null)
  const [stockInItems, setStockInItems] = useState<any[]>([])
  const [stockInRemark, setStockInRemark] = useState('')

  const handleOpenStockInConfirm = (receipt: PendingReceipt) => {
    const items = receipt.items.filter((item: any) => (item.pending_qty || item.quantity) > 0)
    if (items.length === 0) { message.warning('没有待入库商品'); return }
    const now = Date.now().toString().slice(-6)
    const enriched = items.map((item: any) => ({ ...item, qty: item.pending_qty || item.quantity, batch_no: `PO${receipt.id}P${item.product_id}${now}` }))
    setStockInReceipt(receipt)
    setStockInItems(enriched)
    setStockInRemark('')
    setStockInConfirmOpen(true)
  }

  const handleConfirmStockIn = async () => {
    if (!stockInReceipt) return
    setPendingConfirming(stockInReceipt.id)
    try {
      let successCount = 0
      for (const item of stockInItems) {
        const res = await inventoryApi.stockIn({
          warehouse_id: stockInReceipt.warehouse_id, product_id: item.product_id, quantity: item.qty,
          unit_price: item.unit_price || 0, batch_no: item.batch_no,
          reference_type: 'purchase_order', reference_id: stockInReceipt.id,
          remark: stockInRemark || undefined,
        })
        if (res.code === 200) successCount++
        else { message.error(`${item.product_name || '商品'}入库失败: ${res.message}`); break }
      }
      if (successCount === stockInItems.length) {
        message.success(`成功入库 ${successCount} 种商品`)
        setStockInConfirmOpen(false)
        fetchPending()
        fetchInventory(pagination.current, pagination.pageSize)
        fetchSummary()
      }
    } catch { message.error('入库失败') }
    setPendingConfirming(null)
  }

  // Stock in (manual)
  const handleStockIn = async (values: any) => {
    try {
      const res = await inventoryApi.stockIn(values)
      if (res.code === 200) {
        message.success('入库成功')
        setInModalOpen(false)
        inForm.resetFields()
        fetchInventory(pagination.current, pagination.pageSize)
        fetchSummary()
      } else { message.error(res.message || '入库失败') }
    } catch { message.error('入库失败') }
  }

  // Stock out
  const handleStockOut = async (values: any) => {
    try {
      const res = await inventoryApi.out(values)
      if (res.code === 200) {
        message.success('出库成功')
        setOutModalOpen(false)
        outForm.resetFields()
        fetchInventory(pagination.current, pagination.pageSize)
        fetchSummary()
      } else { message.error(res.message || '出库失败') }
    } catch { message.error('出库失败') }
  }

  // Adjust
  const handleAdjust = async (values: any) => {
    try {
      const res = await inventoryApi.adjust(values)
      if (res.code === 200) {
        message.success('调整成功')
        setAdjustModalOpen(false)
        adjustForm.resetFields()
        fetchInventory(pagination.current, pagination.pageSize)
        fetchSummary()
      } else { message.error(res.message || '调整失败') }
    } catch { message.error('调整失败') }
  }

  // Report loss
  const handleReportLoss = async (record: InventoryItem) => {
    confirm({
      title: '报损登记',
      icon: <ExclamationCircleOutlined />,
      content: (
        <Form form={adjustForm} layout="vertical">
          <Form.Item name="product_id" initialValue={record.product_id}><Input type="hidden" /></Form.Item>
          <Form.Item name="warehouse_id" initialValue={record.warehouse_id}><Input type="hidden" /></Form.Item>
          <Form.Item name="batch_no" initialValue={record.batch_no}><Input type="hidden" /></Form.Item>
          <Form.Item name="quantity" label="报损数量" rules={[{ required: true }]}><InputNumber min={1} max={parseFloat(record.quantity || '0')} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="remark" label="原因"><Input.TextArea rows={2} placeholder="请输入报损原因" /></Form.Item>
        </Form>
      ),
      onOk: async () => {
        const values = adjustForm.getFieldsValue()
        values.reason = values.remark
        try {
          const res = await inventoryApi.reportLoss(values)
          if (res.code === 200) { message.success('报损已提交'); adjustForm.resetFields() }
          else message.error(res.message || '提交失败')
        } catch { message.error('提交失败') }
      },
    })
  }

  // Audit loss
  const handleAuditLoss = async (logId: number, action: string) => {
    try {
      const res = await inventoryApi.auditLoss({ log_id: logId, action })
      if (res.code === 200) {
        message.success(action === 'approve' ? '审核通过，库存已扣减' : '已驳回')
        fetchLossPending()
        fetchInventory(pagination.current, pagination.pageSize)
        fetchLogs(logPagination.current, logPagination.pageSize)
      } else { message.error(res.message || '审核失败') }
    } catch { message.error('审核失败') }
  }

  // View batch detail
  const handleViewBatches = async (record: any) => {
    const params = new URLSearchParams({ warehouse_id: record.warehouse_id, product_id: record.product_id })
    try {
      const res = await fetch('/api/inventory/batches?' + params).then(r => r.json())
      if (res.code === 200) {
        setBatchDetail({ product_name: record.product_name, batches: res.data || [] })
        setBatchModalOpen(true)
      }
    } catch { message.error('获取批次详情失败') }
  }

  const logTypeText = (t: number) => t === 1 ? '入库' : t === 2 ? '出库' : t === 3 ? '调整' : t === 4 ? '报损' : '未知'
  const logTypeColor = (t: number) => t === 1 ? 'green' : t === 2 ? 'blue' : t === 3 ? 'orange' : 'red'

  const batchColumns: ColumnsType<any> = [
    { title: '批次号', dataIndex: 'batch_no', key: 'batch_no' },
    { title: '库存(500克)', dataIndex: 'quantity', key: 'quantity', render: v => <b>{v}</b> },
    { title: '销售数量', key: 'sale_qty', render: (_, r) => {
      const weight = parseFloat(r.sale_unit_weight || 0)
      const qty = parseFloat(r.quantity || 0)
      if (!weight || !qty) return '-'
      return `${(qty / weight).toFixed(1)} ${r.sale_unit || ''}`
    }},
    { title: '成本价(克)', dataIndex: 'cost_price', key: 'cost_price', render: v => v ? `\u00a5${parseFloat(v).toFixed(4)}` : '-' },
    { title: '到期日期', dataIndex: 'expire_date', key: 'expire_date', render: v => v ? (new Date(v).getTime() < Date.now() ? <Tag color="red">{new Date(v).toLocaleDateString()}</Tag> : new Date(v).toLocaleDateString()) : '-' },
    { title: '入库时间', dataIndex: 'created_at', key: 'created_at', render: v => v ? new Date(v).toLocaleString() : '-' },
  ]

  const inventoryColumns: ColumnsType<any> = [
    { title: '商品编码', key: 'code', render: (_, r) => r.product?.code || '-' },
    { title: '商品名称', key: 'name', render: (_, r) => r.product?.name || '-' },
    { title: '仓库', key: 'warehouse', render: (_, r) => r.warehouse?.name || '-' },
    {
      title: '总库存(500克)', dataIndex: 'total_quantity', key: 'total_quantity',
      render: v => <span style={{ color: (v || 0) <= 0 ? 'red' : '#55266c', fontWeight: 'bold' }}>{v}</span>,
    },
    { title: '销售单位', key: 'sale_unit', render: (_, r) => r.product?.sale_unit || '-', width: 90 },
    { title: '批次数', dataIndex: 'batch_count', key: 'batch_count', render: v => <Tag>{v}</Tag> },
    {
      title: '最早到期', dataIndex: 'earliest_expire', key: 'earliest_expire',
      render: v => v ? (new Date(v).getTime() < Date.now() ? <Tag color="red">{new Date(v).toLocaleDateString()}</Tag> : new Date(v).toLocaleDateString()) : '-',
    },
    {
      title: '操作', key: 'action', width: 180,
      render: (_, r) => (
        <Space size="small">
          <Button type="link" size="small" icon={<ArrowUpOutlined />} onClick={() => { outForm.setFieldsValue({ product_id: r.product_id, warehouse_id: r.warehouse_id }); setOutModalOpen(true) }}>出库</Button>
          <Button type="link" size="small" icon={<ArrowDownOutlined />} onClick={() => { inForm.setFieldsValue({ product_id: r.product_id, warehouse_id: r.warehouse_id }); setInModalOpen(true) }}>入库</Button>
          <Button type="link" danger size="small" icon={<ExclamationCircleOutlined />} onClick={() => handleReportLoss(r)}>报损</Button>
        </Space>
      ),
    },
  ]

  const pendingItemColumns: ColumnsType<any> = [
    { title: '商品名称', dataIndex: 'product_name', key: 'product_name' },
    { title: '规格', dataIndex: 'specs', key: 'specs', render: v => { 
    try { 
      const s = typeof v === 'string' ? JSON.parse(v) : v; 
      return Array.isArray(s) ? s.map((x:any) => x?.name).filter(Boolean).join(', ') : (s?.name || '-'); 
    } catch { 
      return (typeof v === 'string' && v.startsWith('{')) ? '(规格)' : (v || '-'); 
    } 
  } },
    { title: '采购数量', dataIndex: 'quantity', key: 'quantity' },
    { title: '已入库', dataIndex: 'received_qty', key: 'received_qty' },
    { title: '待入库', dataIndex: 'pending_qty', key: 'pending_qty', render: v => <b style={{ color: v > 0 ? '#1890ff' : '#999' }}>{v}</b> },
    { title: '采购单价', dataIndex: 'unit_price', key: 'unit_price', render: v => v ? `\u00a5${v}` : '-' },
    { title: '小计', dataIndex: 'total_price', key: 'total_price', render: v => v ? `\u00a5${v.toFixed(2)}` : '-' },
  ]

  const pendingColumns: ColumnsType<PendingReceipt> = [
    { title: '采购单号', dataIndex: 'order_no', key: 'order_no' },
    { title: '状态', dataIndex: 'status', key: 'status', render: t => t === 2 ? <Tag color="blue">待入库</Tag> : t === 3 ? <Tag color="orange">部分入库</Tag> : <Tag>{t}</Tag> },
    { title: '预计到货', dataIndex: 'expected_date', key: 'expected_date', render: v => v || '-' },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', render: v => v ? new Date(v).toLocaleString() : '-' },
    {
      title: '商品明细', key: 'items',
      render: (_, r) => {
        const pendingItems = r.items?.filter((i: any) => (i.pending_qty || i.quantity) > 0) || []
        return pendingItems.length > 0
          ? <span style={{ color: '#1890ff' }}>{pendingItems.length}种商品待入库</span>
          : <span style={{ color: '#999' }}>全部已入库</span>
      },
    },
    {
      title: '操作', key: 'action', width: 140,
      render: (_, r) => {
        const hasPending = r.items?.some((i: any) => (i.pending_qty || i.quantity) > 0)
        return (
          <Button type="primary" size="small" icon={<InboxOutlined />} disabled={!hasPending} onClick={() => handleOpenStockInConfirm(r)}>
            确认入库
          </Button>
        )
      },
    },
  ]

  const lossColumns: ColumnsType<LossItem> = [
    { title: '提交时间', dataIndex: 'created_at', key: 'created_at', render: v => v ? new Date(v).toLocaleString() : '-' },
    { title: '仓库', dataIndex: 'warehouse_name', key: 'warehouse_name', render: t => t || '-' },
    { title: '商品', dataIndex: 'product_name', key: 'product_name', render: t => t || '-' },
    { title: '批次号', dataIndex: 'batch_no', key: 'batch_no', render: t => t || '-' },
    {
      title: '报损数量', dataIndex: 'quantity', key: 'quantity',
      render: v => <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{Math.abs(parseFloat(v || '0'))}</span>,
    },
    { title: '单价', dataIndex: 'unit_price', key: 'unit_price', render: v => v ? `¥${v}` : '-' },
    { title: '金额', dataIndex: 'total_amount', key: 'total_amount', render: v => v ? `¥${v}` : '-' },
    { title: '原因', dataIndex: 'remark', key: 'remark', render: t => t || '-' },
    {
      title: '操作', key: 'action', width: 150,
      render: (_, r) => isSuperAdmin ? (
        <Space>
          <Popconfirm title="确认通过报损？库存将扣减" onConfirm={() => handleAuditLoss(r.id, 'approve')}>
            <Button type="link" icon={<CheckOutlined />} size="small" style={{ color: '#52c41a' }}>通过</Button>
          </Popconfirm>
          <Popconfirm title="确认驳回？" onConfirm={() => handleAuditLoss(r.id, 'reject')}>
            <Button type="link" danger icon={<CloseOutlined />} size="small">驳回</Button>
          </Popconfirm>
        </Space>
      ) : <Tag>待审核</Tag>,
    },
  ]

  const logColumns: ColumnsType<StockLog> = [
    { title: '时间', dataIndex: 'created_at', key: 'created_at', render: v => v ? new Date(v).toLocaleString() : '-' },
    { title: '仓库', dataIndex: 'warehouse_name', key: 'warehouse_name', render: t => t || '-' },
    { title: '商品', dataIndex: 'product_name', key: 'product_name', render: t => t || '-' },
    { title: '批次号', dataIndex: 'batch_no', key: 'batch_no', render: t => t || '-' },
    { title: '类型', dataIndex: 'log_type', key: 'log_type', render: v => <Tag color={logTypeColor(v)}>{logTypeText(v)}</Tag> },
    { title: '数量', dataIndex: 'quantity', key: 'quantity', render: v => v },
    { title: '单价', dataIndex: 'unit_price', key: 'unit_price', render: v => v ? `¥${v}` : '-' },
    { title: '原因', dataIndex: 'remark', key: 'remark', render: t => t || '-' },
    {
      title: '审核', dataIndex: 'status', key: 'status',
      render: v => v === 0 ? <Tag color="orange">待审核</Tag> : v === 1 ? <Tag color="green">已审核</Tag> : v === 2 ? <Tag>已驳回</Tag> : '-',
    },
    { title: '审核人', dataIndex: 'auditor_name', key: 'auditor_name', render: t => t || '-' },
  ]

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 16 }}>库存管理</h2>

      <Space style={{ marginBottom: 16 }}>
        <Button type={activeTab === 'list' ? 'primary' : 'default'} onClick={() => setActiveTab('list')}>库存列表</Button>
        <Badge count={pendingReceipts.length} size="small" offset={[-2, 2]}>
          <Button type={activeTab === 'pending' ? 'primary' : 'default'} onClick={() => setActiveTab('pending')}>待入库</Button>
        </Badge>
        <Button type={activeTab === 'loss' ? 'primary' : 'default'} onClick={() => setActiveTab('loss')}>报损审核</Button>
        <Button type={activeTab === 'logs' ? 'primary' : 'default'} onClick={() => setActiveTab('logs')}>操作日志</Button>
      </Space>

      {activeTab !== 'loss' && activeTab !== 'pending' && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}><b>商品种类：</b>{summaryData.length}</Col>
            <Col span={6}><b>总库存(500克)：</b>{summaryData.reduce((s, r) => s + (r.total_quantity || 0), 0)}</Col>
            <Col span={6}><b>仓库数量：</b>{warehouses.length}</Col>
          </Row>
        </Card>
      )}

      {activeTab !== 'loss' && activeTab !== 'pending' && (
        <Space style={{ marginBottom: 12 }}>
          <Select placeholder="选择仓库" style={{ width: 160 }} allowClear value={selectedWarehouse} onChange={v => { setSelectedWarehouse(v); fetchInventory(1, pagination.pageSize) }}>
            {warehouses.map(w => <Select.Option key={w.id} value={w.id}>{w.name}</Select.Option>)}
          </Select>
          <Search placeholder="搜索商品" style={{ width: 200 }} onSearch={v => { setKeyword(v); fetchInventory(1, pagination.pageSize) }} />

          <Button icon={<ArrowUpOutlined />} onClick={() => setOutModalOpen(true)}>手动出库</Button>
          <Button icon={<ArrowDownOutlined />} onClick={() => setAdjustModalOpen(true)}>库存调整</Button>
        </Space>
      )}

      {activeTab === 'list' && (
        <Table
          columns={inventoryColumns}
          dataSource={inventoryData}
          rowKey={r => `${r.product_id}_${r.warehouse_id}`}
          loading={loading}
          expandable={{
            expandedRowRender: (record: any) => (
              <div style={{ padding: '0 48px' }}>
                <div style={{ marginBottom: 8, color: '#666' }}><b>{record.product?.name}</b> 的所有批次详情：</div>
                <Table
                  columns={batchColumns}
                  dataSource={record.batches || []}
                  rowKey="id"
                  size="small"
                  pagination={false}
                />
              </div>
            ),
            rowExpandable: (record: any) => (record.batches || []).length > 0,
          }}
          pagination={{ current: pagination.current, pageSize: pagination.pageSize, total: pagination.total, showSizeChanger: true, showTotal: t => `共${t}条` }}
          onChange={p => fetchInventory(p.current || 1, p.pageSize || 20)}
        />
      )}

      {activeTab === 'pending' && (
        <Table
          columns={pendingColumns}
          dataSource={pendingReceipts}
          rowKey="id"
          loading={pendingLoading}
          expandable={{
            expandedRowRender: (record: PendingReceipt) => (
              <Table
                columns={pendingItemColumns}
                dataSource={record.items}
                rowKey={(r: any) => r.product_id}
                size="small"
                pagination={false}
              />
            ),
            rowExpandable: (record: PendingReceipt) => record.items?.length > 0,
          }}
        />
      )}

      {activeTab === 'loss' && (
        <Table columns={lossColumns} dataSource={lossData} rowKey="id" loading={lossLoading} />
      )}

      {activeTab === 'logs' && (
        <Table
          columns={logColumns}
          dataSource={logData}
          rowKey="id"
          loading={loading}
          pagination={{ current: logPagination.current, pageSize: logPagination.pageSize, total: logPagination.total, showSizeChanger: true, showTotal: t => `共${t}条` }}
          onChange={p => fetchLogs(p.current || 1, p.pageSize || 20)}
        />
      )}

      {/* 手动入库 */}
      <Modal title="手动入库" open={inModalOpen} onOk={() => inForm.submit()} onCancel={() => { setInModalOpen(false); inForm.resetFields() }}>
        <Form form={inForm} layout="vertical" onFinish={handleStockIn}>
          <Form.Item name="warehouse_id" label="仓库" rules={[{ required: true }]}>
            <Select placeholder="请选择仓库">
              {warehouses.map(w => <Select.Option key={w.id} value={w.id}>{w.name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="product_id" label="商品ID" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} placeholder="请输入商品ID" />
          </Form.Item>
          <Form.Item name="batch_no" label="批次号"><Input placeholder="批次号（可选）" /></Form.Item>
          <Form.Item name="quantity" label="入库数量（克）" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} placeholder="请输入克数" />
          </Form.Item>
          <Form.Item name="unit_price" label="成本单价（元/克）"><InputNumber min={0} step={0.001} style={{ width: '100%' }} placeholder="成本单价（可选）" /></Form.Item>
          <Form.Item name="sale_unit" label="销售单位"><Input placeholder="如：500克、包、瓶（可选）" /></Form.Item>
          <Form.Item name="sale_unit_weight" label="每单位克重"><InputNumber min={1} style={{ width: '100%' }} placeholder="如500克单位填500（可选）" /></Form.Item>
          <Form.Item name="expiry_date" label="到期日期"><Input type="date" /></Form.Item>
        </Form>
      </Modal>

      {/* 手动出库 */}
      <Modal title="手动出库" open={outModalOpen} onOk={() => outForm.submit()} onCancel={() => { setOutModalOpen(false); outForm.resetFields() }}>
        <Form form={outForm} layout="vertical" onFinish={handleStockOut}>
          <Form.Item name="warehouse_id" label="仓库" rules={[{ required: true }]}>
            <Select placeholder="请选择仓库">
              {warehouses.map(w => <Select.Option key={w.id} value={w.id}>{w.name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="product_id" label="商品ID" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} placeholder="请输入商品ID" />
          </Form.Item>
          <Form.Item name="quantity" label="数量" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} placeholder="请输入数量" />
          </Form.Item>
          <Form.Item name="remark" label="原因"><Input.TextArea rows={2} placeholder="出库原因" /></Form.Item>
        </Form>
      </Modal>

      {/* 库存调整 */}
      <Modal title="库存调整" open={adjustModalOpen} onOk={() => adjustForm.submit()} onCancel={() => { setAdjustModalOpen(false); adjustForm.resetFields() }}>
        <Form form={adjustForm} layout="vertical" onFinish={handleAdjust}>
          <Form.Item name="warehouse_id" label="仓库" rules={[{ required: true }]}>
            <Select placeholder="请选择仓库">
              {warehouses.map(w => <Select.Option key={w.id} value={w.id}>{w.name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="product_id" label="商品ID" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} placeholder="请输入商品ID" />
          </Form.Item>
          <Form.Item name="adjust_type" label="调整类型" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="add">盘盈（增加）</Select.Option>
              <Select.Option value="reduce">盘亏（减少）</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="quantity" label="调整数量" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} placeholder="请输入数量" />
          </Form.Item>
          <Form.Item name="remark" label="原因"><Input.TextArea rows={2} placeholder="调整原因" /></Form.Item>
        </Form>
      </Modal>

      {/* 入库确认弹窗 */}
      <Modal
        title="入库确认"
        open={stockInConfirmOpen}
        onOk={handleConfirmStockIn}
        onCancel={() => setStockInConfirmOpen(false)}
        confirmLoading={!!pendingConfirming}
        width={750}
        okText="确认入库"
        cancelText="取消"
      >
        <div style={{ marginBottom: 12 }}>
          <Space size={32}>
            <span><b>采购单号：</b>{stockInReceipt?.order_no}</span>
            <span><b>创建时间：</b>{stockInReceipt?.created_at ? new Date(stockInReceipt.created_at).toLocaleString() : '-'}</span>
            <span><b>预计到货：</b>{stockInReceipt?.expected_date || '-'}</span>
          </Space>
        </div>
        <Divider style={{ margin: '8px 0 16px' }} />
        <Table
          columns={[
            { title: '序号', key: 'idx', width: 50, render: (_, __, i) => i + 1 },
            { title: '商品名称', dataIndex: 'product_name', key: 'product_name' },
            { title: '规格', dataIndex: 'specs', key: 'specs', render: v => { 
    try { 
      const s = typeof v === 'string' ? JSON.parse(v) : v; 
      return Array.isArray(s) ? s.map((x:any) => x?.name).filter(Boolean).join(', ') : (s?.name || '-'); 
    } catch { 
      return (typeof v === 'string' && v.startsWith('{')) ? '(规格)' : (v || '-'); 
    } 
  } },
            { title: '采购数量', dataIndex: 'quantity', key: 'quantity' },
            { title: '待入库', dataIndex: 'qty', key: 'qty', render: v => <b style={{ color: '#1890ff' }}>{v}</b> },
            { title: '备注', key: 'remark', render: (_, r) => r.remark || '-' },
            { title: '批次号', dataIndex: 'batch_no', key: 'batch_no', render: v => <Tag color="blue" style={{ fontSize: 11 }}>{v}</Tag> },
          ]}
          dataSource={stockInItems}
          rowKey={(r: any) => r.product_id}
          size="small"
          pagination={false}
        />
        <div style={{ marginTop: 12 }}>
          <b>备注：</b>
          <Input.TextArea rows={2} placeholder="入库备注（可选）" value={stockInRemark} onChange={e => setStockInRemark(e.target.value)} style={{ marginTop: 4 }} />
        </div>
      </Modal>

      {/* 批次详情 */}
      <Modal title={`批次详情 - ${batchDetail?.product_name || ''}`} open={batchModalOpen} onCancel={() => setBatchModalOpen(false)} footer={null} width={600}>
        <Table
          columns={[
            { title: '批次号', dataIndex: 'batch_no', key: 'batch_no' },
            { title: '数量', dataIndex: 'quantity', key: 'quantity' },
            { title: '成本价', dataIndex: 'cost_price', key: 'cost_price', render: v => `¥${v}` },
            { title: '到期日期', dataIndex: 'expiry_date', key: 'expiry_date', render: v => v || '-' },
            { title: '状态', dataIndex: 'status', key: 'status', render: v => v === 1 ? '正常' : v === 0 ? '冻结' : '其他' },
          ]}
          dataSource={batchDetail?.batches || []}
          rowKey="batch_no"
          size="small"
          pagination={false}
        />
      </Modal>
    </div>
  )
}

export default Inventory